import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { PatientAttachment } from 'app/core/models/patient-attachment.model';
import { PatientAttachmentsService } from 'app/core/services/patient-attachments.service';
import { NavigationService } from 'app/core/services/navigation.service';
import { AuthenticationService } from 'app/core/services/authentication.service';
import { UserTimingService } from 'app/core/services/user-timing.service';
import { PatientAttachmentHistoryItemActions } from 'app/core/enums/patient-attachment-history-item-actions.enum';
import { PatientAttachmentHistoryItem } from 'app/core/models/patient-attachment-history-item.model';
import { UserPresenceService } from 'app/core/services/user-presence.service';
import { DatePipe } from '@angular/common';
import { ConsultNoteTitleComponent } from '../../components/consult-note-title/consult-note-title.component';
import { ConsultNoteHeaderComponent } from '../../components/consult-note-header/consult-note-header.component';
import { ConsultNoteHistoryItemTypeCreatedComponent } from '../../components/consult-note-history-item-type-created/consult-note-history-item-type-created.component';
import { ConsultNoteHistoryItemTypeTransitionedComponent } from '../../components/consult-note-history-item-type-transitioned/consult-note-history-item-type-transitioned.component';
import { ConsultNoteHistoryItemTypeSubmittedComponent } from '../../components/consult-note-history-item-type-submitted/consult-note-history-item-type-submitted.component';
import { ConsultNoteHistoryItemTypeAssignmentNewComponent } from '../../components/consult-note-history-item-type-assignment-new/consult-note-history-item-type-assignment-new.component';
import { ConsultNoteHistoryItemTypeAssignmentUpdatedComponent } from '../../components/consult-note-history-item-type-assignment-updated/consult-note-history-item-type-assignment-updated.component';
import { ConsultNoteHistoryItemTypeAssignmentRemovedComponent } from '../../components/consult-note-history-item-type-assignment-removed/consult-note-history-item-type-assignment-removed.component';
import { ConsultNoteHistoryItemTypeBaseComponent } from '../../components/consult-note-history-item-type-base/consult-note-history-item-type-base.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { HistoryItemUserNamePipe } from '../../../shared/pipes/history-item-user-name.pipe';

@Component({
    selector: 'app-consult-note-history',
    templateUrl: './consult-note-history.component.html',
    styleUrls: ['./consult-note-history.component.scss'],
    imports: [ConsultNoteTitleComponent,
              ConsultNoteHeaderComponent,
              ConsultNoteHistoryItemTypeCreatedComponent,
              ConsultNoteHistoryItemTypeTransitionedComponent,
              ConsultNoteHistoryItemTypeSubmittedComponent,
              ConsultNoteHistoryItemTypeAssignmentNewComponent,
              ConsultNoteHistoryItemTypeAssignmentUpdatedComponent,
              ConsultNoteHistoryItemTypeAssignmentRemovedComponent,
              ConsultNoteHistoryItemTypeBaseComponent,
              MatProgressSpinner,
              DatePipe,
              HistoryItemUserNamePipe]
})
export class ConsultNoteHistoryComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private patientAttachmentsService = inject(PatientAttachmentsService);
  private navigationService = inject(NavigationService);
  private titleService = inject(Title);
  private authService = inject(AuthenticationService);
  private userTimingService = inject(UserTimingService);
  private router = inject(Router);
  private userPresenceService = inject(UserPresenceService);

  patientAttachmentId: string;
  patientAttachment: PatientAttachment;
  refreshing = true;

  loadingHistoryItems = false;
  patientAttachmentHistoryItems: PatientAttachmentHistoryItem[] = [];
  patientAttachmentHistoryItemActions = PatientAttachmentHistoryItemActions;

  constructor() {
    this.route.paramMap.subscribe(paramsMap => {
      this.handleRouteChange(paramsMap);
    });
  }

  ngOnInit() {
    this.patientAttachmentId = this.route.snapshot.paramMap.get('id');
    this.loadPatientAttachment(this.patientAttachmentId);
    this.loadHistoryItems(this.patientAttachmentId);
    this.userPresenceService.subscribeAndJoin("consultNote", this.patientAttachmentId)
  }

  ngOnDestroy() {
    this.stopTimeTracking();
    this.userPresenceService.leaveAndUnsubscribe("consultNote", this.patientAttachmentId)
  }

  private loadPatientAttachment(patientAttachmentId): void {
    this.refreshing = true;
    this.patientAttachmentsService.get(patientAttachmentId).subscribe(
      (patientAttachment: PatientAttachment) => {
        this.refreshing = false;
        this.patientAttachment = patientAttachment;
        this.startTimeTracking(this.patientAttachment);
        this.setTitle();
      },
      (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.navigationService.notFound();
        } else {
          console.error(err);
        }
      }
    );

    window.scrollTo(0, 0);
  }

  private loadHistoryItems(patientAttachmentId): void {
    this.loadingHistoryItems = true;
    this.patientAttachmentsService.getPatientAttachmentHistoryItems(patientAttachmentId).subscribe(
      patientAttachmentHistoryItems => {
        this.loadingHistoryItems = false;
        this.patientAttachmentHistoryItems = patientAttachmentHistoryItems.reverse();
      },
      err => {
        if (err.status === 404) {
          this.router.navigate(['/404']);
        } else {
          console.error(err);
        }
      }
    );
  }

  private setTitle() {
    const title = `NuvemRX 340B Recovery - ${this.patientAttachment.id} | History`;
    this.titleService.setTitle(title);
  }

  private handleRouteChange(paramsMap) {
    const params = paramsMap['params'];
    const patientAttachmentId = params && params['id'];

    if (this.navigationService.shouldReloadPage(this.patientAttachment, patientAttachmentId)) {
      this.loadPatientAttachment(patientAttachmentId);
    }
  }

  private startTimeTracking(patientAttachment) {
    if (this.authService.isCaptureAdminUser) {
      this.userTimingService.track({
        page: 'consult_note_prescriptions',
        recordId: patientAttachment.id,
        status: patientAttachment.status,
      });
    }
  }

  private stopTimeTracking() {
    if (this.authService.isCaptureAdminUser) {
      this.userTimingService.stop();
    }
  }
}
